/**
 * The guide itself, drawn.
 *
 * The body is HTML written in the repo or by a colleague, so it goes in with
 * `dangerouslySetInnerHTML` - and that is only defensible because of who can
 * write it: this text comes from the repository and from staff who hold
 * `manage:crm.kb`, never from a customer, a form or an inbox. If the handbook
 * ever accepts text from outside that circle, it needs a sanitiser first.
 *
 * Two things happen around the raw markup:
 *
 *   - the picture slots are cut out and rendered as components (see lib.ts), so
 *     a hole can carry a button rather than being invisible; and
 *   - a click on a link to another guide is turned into a route change, because
 *     an `<a href>` inside injected HTML would otherwise reload the whole app.
 */
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { KbArticle } from "../../../lib/kb";
import { splitBody } from "./lib";
import { ShotSlot } from "./ShotSlot";

export function ArticleBody({
  article,
  canEdit,
  onPick,
  onClear,
}: {
  article: KbArticle;
  canEdit: boolean;
  onPick: (key: string) => void;
  onClear: (key: string) => void;
}) {
  const navigate = useNavigate();

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const link = (e.target as HTMLElement).closest("a");
      const href = link?.getAttribute("href");
      if (!href) return;
      // Only our own guides. An outside link keeps its ordinary behaviour, and
      // a modified click still means "open this somewhere else".
      if (!href.startsWith("/guides/")) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      navigate(href);
    },
    [navigate],
  );

  return (
    <div className="crm-doc" onClick={onClick}>
      {splitBody(article.body).map((seg, i) =>
        seg.kind === "html" ? (
          <div key={i} dangerouslySetInnerHTML={{ __html: seg.html }} />
        ) : (
          <ShotSlot
            key={`${seg.key}-${i}`}
            slotKey={seg.key}
            caption={seg.caption}
            shot={article.shots[seg.key]}
            canEdit={canEdit}
            onPick={onPick}
            onClear={onClear}
          />
        ),
      )}
    </div>
  );
}
